// src/pages/projects/ProjectChecklist.jsx
//
// Hierarchical, editable project checklist.
// - Owner/Manager/SM: full tree, can add/rename/delete categories,
//   sub-categories (unlimited depth), and items; can toggle which
//   top-level categories are visible to technicians.
// - Technician: sees only the subtree under top-level categories marked
//   tech_visible (defaults to "Execution"); can only tick items and
//   add remarks — no structural edit controls.
//
// Data model: project_checklist_items (flat collection), each doc has
// parent_id (null = top-level category) + node_type ("category"|"item").
// Tree is built client-side via buildChecklistTree(). Legacy (pre-hierarchy)
// projects are auto-migrated on first load — see migrateLegacyChecklistToHierarchy
// in projectService.js.

import { useEffect, useState } from "react";
import { doc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchChecklistTree, updateChecklistItem,
  addChecklistNode, renameChecklistNode, setCategoryTechVisibility,
  findLinkedStageNames, deleteChecklistNodes, collectDescendantIds,
  convertItemToCategory, swapChecklistNodeOrder, TEMPLATE_MAP,
} from "./projectService";
import { checkAndUnlockStages } from "./projectMaterialService";
import { canAccessProject } from "./projectAccess";

const CATEGORY_ICONS = {
  approval:      "✅",
  execution:     "🔧",
  payment:       "💰",
  documentation: "📄",
};

// ── Pure helpers (also unit-tested standalone before shipping) ───────────────
function flattenLeaves(nodes) {
  let leaves = [];
  nodes.forEach(n => {
    if (n.node_type === "item") leaves.push(n);
    if (n.children?.length) leaves = leaves.concat(flattenLeaves(n.children));
  });
  return leaves;
}

function countProgress(node) {
  if (node.node_type === "item") {
    return { total: 1, completed: node.status === "completed" ? 1 : 0 };
  }
  let total = 0, completed = 0;
  (node.children || []).forEach(child => {
    const c = countProgress(child);
    total += c.total;
    completed += c.completed;
  });
  return { total, completed };
}

function getEffectiveOrder(node) {
  return node.order ?? node.item_no ?? 0;
}

// Filters the tree down to only top-level categories marked tech_visible
// (and everything beneath them). Used for the technician view.
function filterTechVisible(nodes) {
  return nodes
    .filter(n => n.node_type !== "category" || n.parent_id !== null || n.tech_visible)
    .map(n => ({ ...n, children: n.children ? filterTechVisible(n.children) : [] }));
}

// embedded: true when rendered as a tab inside ProjectDetail (hides this
// page's own back-link and outer padding). Standalone route unchanged.
export default function ProjectChecklist({ embedded = false }) {
  const { id }   = useParams();
  const navigate = useNavigate();
  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";
  const isManager = ["sm", "owner", "manager"].includes(user.role);
  const isTech    = user.role === "technician";

  const [project,   setProject]   = useState(null);
  const [tree,       setTree]     = useState([]);
  const [loading,    setLoading]  = useState(true);
  const [updating,   setUpdating] = useState(null);
  const [remarksOpen, setRemarksOpen] = useState(null);
  const [remarkText,  setRemarkText]  = useState("");
  const [assignOpen,  setAssignOpen]  = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [collapsed,   setCollapsed]   = useState(new Set()); // node ids collapsed
  const [addFormOpen,  setAddFormOpen]  = useState(null); // parent_id currently showing add-form, or "root"
  const [addNodeType,  setAddNodeType]  = useState("category");
  const [addName,      setAddName]      = useState("");
  const [renamingId,   setRenamingId]   = useState(null);
  const [renameText,   setRenameText]   = useState("");

  // loadChecklist() runs only after fetchProject() has passed the access
  // check — so an unassigned user never sees the tree behind the alert.
  useEffect(() => { if (id) fetchProject(); }, [id]); // eslint-disable-line

  const fetchProject = async () => {
    try {
      const snap = await getDoc(doc(db, "projects", id));
      if (snap.exists()) {
        const data = snap.data();
        // Company + assignment check (shared rule — projectAccess.js).
        // Also closes direct-URL access to /projects/:id/checklist.
        if (!(await canAccessProject(data, snap.id, user))) {
          alert("Access denied — you are not assigned to this project");
          navigate("/projects");
          return;
        }
        setProject({ id: snap.id, ...data });
        fetchTeamMembers(company_id);
        loadChecklist();
      } else {
        setLoading(false);
      }
    } catch (err) { console.error(err); setLoading(false); }
  };

  const fetchTeamMembers = async (cid) => {
    try {
      const { getDocs: gd, query: q, collection: col, where: w } =
        await import("firebase/firestore");
      const snap = await gd(q(col(db, "users"), w("company_id", "==", cid)));
      setTeamMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error("fetchTeamMembers:", err); }
  };

  const loadChecklist = async () => {
    try {
      setLoading(true);
      const result = await fetchChecklistTree(id, company_id, user);
      setTree(result);
    } catch (err) {
      console.error("loadChecklist error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Used after any in-place action (tick, add, delete, rename, reorder...).
  // Unlike loadChecklist(), this never touches `loading` — so the page never
  // collapses to the tiny "Loading..." view mid-action, which was causing
  // the scroll position to jump back to top after every edit.
  const refreshChecklist = async () => {
    try {
      const result = await fetchChecklistTree(id, company_id, user);
      setTree(result);
    } catch (err) {
      console.error("refreshChecklist error:", err);
    }
  };

  // ── Status cycle (item nodes only) ──────────────────────────────────────
  const cycleStatus = async (item) => {
    const next = {
      pending:   "completed",
      completed: "n/a",
      "n/a":     "pending",
    }[item.status] || "completed";

    setUpdating(item.id);
    try {
      await updateChecklistItem(item.id, {
        status:            next,
        completed_by:      next === "completed" ? (user.uid || user.id || "") : "",
        completed_by_name: next === "completed" ? (user.name || "") : "",
        completed_on:      next === "completed" ? serverTimestamp() : null,
      });

      if (next === "completed") {
        try {
          const unlocked = await checkAndUnlockStages(company_id, id, {
            id: item.id, item_no: item.item_no, item_name: item.item_name,
          });
          if (unlocked.length > 0) {
            alert(`🔓 Material Stage Unlocked: ${unlocked.join(", ")}`);
          }
        } catch (err) {
          console.error("checkAndUnlockStages failed:", err);
        }
      }
       await refreshChecklist();
    } catch (err) {
      alert("Failed to update item");
    } finally {
      setUpdating(null);
    }
  };

  const handleAssign = async (itemId, member) => {
    try {
      await updateChecklistItem(itemId, {
        assigned_to:      member.id,
        assigned_to_name: member.name,
      });
      setAssignOpen(null);
       await refreshChecklist();
    } catch (err) { alert("Failed to assign"); }
  };

  const handleSaveRemark = async (itemId) => {
    if (!remarkText.trim()) { setRemarksOpen(null); return; }
    try {
      await updateChecklistItem(itemId, { remarks: remarkText.trim() });
      setRemarksOpen(null);
      setRemarkText("");
       await refreshChecklist();
    } catch (err) { alert("Failed to save remark"); }
  };

  // ── Structural edits (owner/manager only) ────────────────────────────────
  const handleAddNode = async (parentId) => {
    if (!addName.trim()) { alert("Name required"); return; }
    try {
      await addChecklistNode({
        project_id:   id,
        project_name: project?.project_name,
        company_id,
        parent_id:    parentId === "root" ? null : parentId,
        node_type:    addNodeType,
        item_name:    addName,
      }, user);
      setAddFormOpen(null);
      setAddName("");
       await refreshChecklist();
    } catch (err) { alert("Failed to add: " + err.message); }
  };

  const handleRename = async (itemId) => {
    if (!renameText.trim()) { setRenamingId(null); return; }
    try {
      await renameChecklistNode(itemId, renameText);
      setRenamingId(null);
      setRenameText("");
       await refreshChecklist();
    } catch (err) { alert("Failed to rename"); }
  };

  const handleToggleTechVisible = async (itemId, current) => {
    try {
      await setCategoryTechVisibility(itemId, !current);
       await refreshChecklist();
    } catch (err) { alert("Failed to update visibility"); }
  };

  const handleDelete = async (node) => {
    const idsToDelete = collectDescendantIds(node);
    try {
      const linkedStages = await findLinkedStageNames(company_id, id, idsToDelete);
      if (linkedStages.length > 0) {
        const proceed = window.confirm(
          `⚠️ "${node.item_name}" (or something inside it) unlocks Material Stage(s): ${linkedStages.join(", ")}.\n\n` +
          `Deleting it means those stages may never unlock automatically.\n\nDelete anyway?`
        );
        if (!proceed) return;
      } else {
        const proceed = window.confirm(`Delete "${node.item_name}"${node.children?.length ? " and everything inside it" : ""}?`);
        if (!proceed) return;
      }
      await deleteChecklistNodes(idsToDelete);
       await refreshChecklist();
    } catch (err) { alert("Failed to delete: " + err.message); }
  };

  // Turns a leaf item into a category so sub-items (e.g. floor-wise
  // breakdown) can be added inside it. Never offered for milestone items.
  const handleConvertToCategory = async (node) => {
    try {
      const linkedStages = await findLinkedStageNames(company_id, id, [node.id]);
      if (linkedStages.length > 0) {
        const proceed = window.confirm(
          `⚠️ "${node.item_name}" unlocks Material Stage(s): ${linkedStages.join(", ")}.\n\n` +
          `Converting it to a category removes its complete/pending status, so it can never trigger that stage's unlock again.\n\nConvert anyway?`
        );
        if (!proceed) return;
      } else {
        const proceed = window.confirm(
          `Convert "${node.item_name}" into a category?\n\nYou'll be able to add sub-items inside it (e.g. "1st Floor", "2nd Floor"). This item's own complete/pending status will be removed.`
        );
        if (!proceed) return;
      }
      await convertItemToCategory(node.id);
       await refreshChecklist();
    } catch (err) { alert("Failed to convert: " + err.message); }
  };

  const handleMoveNode = async (node, siblings, index, direction) => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= siblings.length) return; // already at boundary
    const other = siblings[swapIndex];
    try {
      await swapChecklistNodeOrder(
        node.id, getEffectiveOrder(other),
        other.id, getEffectiveOrder(node)
      );
       await refreshChecklist();
    } catch (err) { alert("Failed to reorder: " + err.message); }
  };

  const toggleCollapse = (nodeId) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId);
      return next;
    });
  };

  if (loading) return <div className="p-6 text-gray-500">Loading checklist...</div>;

  // ── Stats (overall) ───────────────────────────────────────────────────────
  const visibleTree = isTech ? filterTechVisible(tree) : tree;
  const leaves    = flattenLeaves(visibleTree);
  const total     = leaves.length;
  const completed = leaves.filter(i => i.status === "completed").length;
  const naItems   = leaves.filter(i => i.status === "n/a").length;
  const applicable = total - naItems;
  const pct = applicable > 0 ? Math.round((completed / applicable) * 100) : 0;

  // Badge only shows when this project genuinely has 2+ distinct trades
  // tagged among its top-level categories — a single-trade project (or any
  // legacy project with no `trade` field at all) never shows it.
  const distinctTrades = new Set(tree.filter(n => n.trade).map(n => n.trade));
  const isMultiTrade = distinctTrades.size > 1;

  // ── Recursive node renderer ───────────────────────────────────────────────
  // siblings/index let each node show Up/Down reorder controls relative to
  // its own sibling group (category or item list it belongs to).
  const renderNode = (node, depth, siblings = [node], index = 0) => {
    const isCollapsed = collapsed.has(node.id);
    const progress = node.node_type === "category" ? countProgress(node) : null;
    const isTopLevelCategory = node.node_type === "category" && node.parent_id === null;
    const isFirst = index === 0;
    const isLast  = index === siblings.length - 1;

    if (node.node_type === "category") {
      return (
        <div key={node.id} className="bg-white rounded-xl shadow-sm border border-gray-100"
          style={{ marginLeft: depth * 16 }}>
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <button onClick={() => toggleCollapse(node.id)} className="text-gray-400 text-xs">
              {isCollapsed ? "▶" : "▼"}
            </button>
            {depth === 0 && <span>{CATEGORY_ICONS[node.category] || "📁"}</span>}

            {isMultiTrade && node.trade && (
              <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] font-semibold uppercase shrink-0">
                {TEMPLATE_MAP[node.trade]?.label || node.trade}
              </span>
            )}

            {renamingId === node.id ? (
              <>
                <input autoFocus value={renameText} onChange={e => setRenameText(e.target.value)}
                  className="border rounded-lg px-2 py-1 text-sm outline-none" />
                <button onClick={() => handleRename(node.id)} className="text-xs text-blue-600 font-semibold">Save</button>
                <button onClick={() => setRenamingId(null)} className="text-xs text-gray-400">Cancel</button>
              </>
            ) : (
              <span className="font-semibold text-gray-700">{node.item_name}</span>
            )}

            <span className="ml-auto text-xs text-gray-400">
              {progress.completed}/{progress.total}
            </span>

            {isManager && isTopLevelCategory && (
              <label className="flex items-center gap-1 text-xs text-gray-500 ml-2 cursor-pointer">
                <input type="checkbox" checked={!!node.tech_visible}
                  onChange={() => handleToggleTechVisible(node.id, node.tech_visible)} />
                Tech visible
              </label>
            )}

            {isManager && (
              <>
                <button onClick={() => handleMoveNode(node, siblings, index, "up")}
                  disabled={isFirst}
                  className="text-gray-300 hover:text-blue-500 text-xs disabled:opacity-20 disabled:hover:text-gray-300 ml-2">↑</button>
                <button onClick={() => handleMoveNode(node, siblings, index, "down")}
                  disabled={isLast}
                  className="text-gray-300 hover:text-blue-500 text-xs disabled:opacity-20 disabled:hover:text-gray-300">↓</button>
                <button onClick={() => { setRenamingId(node.id); setRenameText(node.item_name); }}
                  className="text-gray-300 hover:text-blue-500 text-xs">✏️</button>
                <button onClick={() => handleDelete(node)}
                  className="text-gray-300 hover:text-red-500 text-xs">🗑</button>
              </>
            )}
          </div>

          {!isCollapsed && (
            <div className="p-3 space-y-2">
              {(node.children || []).map((child, childIndex) => renderNode(child, depth + 1, node.children, childIndex))}

              {isManager && (
                addFormOpen === node.id ? (
                  <div className="pl-2">
                    <p className="text-[11px] text-gray-400 mb-1">
                      Adding inside <span className="font-semibold text-gray-600">"{node.item_name}"</span>:
                    </p>
                    <div className="flex gap-2 items-center">
                      <select value={addNodeType} onChange={e => setAddNodeType(e.target.value)}
                        className="border rounded-lg text-xs p-1.5 outline-none">
                        <option value="category">📁 New Category (can hold sub-items)</option>
                        <option value="item">✅ New Item (a task to tick off)</option>
                      </select>
                      <input autoFocus value={addName} onChange={e => setAddName(e.target.value)}
                        placeholder="Name..."
                        className="flex-1 border rounded-lg text-xs p-1.5 outline-none" />
                      <button onClick={() => handleAddNode(node.id)}
                        className="px-2 py-1 bg-blue-600 text-white rounded-lg text-xs font-semibold">Add</button>
                      <button onClick={() => { setAddFormOpen(null); setAddName(""); }}
                        className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setAddFormOpen(node.id); setAddNodeType("item"); }}
                    className="text-xs text-blue-500 hover:underline pl-2">
                    + Add inside "{node.item_name}"
                  </button>
                )
              )}
            </div>
          )}
        </div>
      );
    }

    // ── Item node ──────────────────────────────────────────────────────────
    return (
      <div key={node.id} className={`p-3 rounded-xl border transition ${
        node.status === "completed" ? "bg-green-50/30 border-green-100" : "bg-white border-gray-100"
      }`} style={{ marginLeft: depth * 16 }}>
        <div className="flex items-start gap-3">
          <button
            onClick={() => cycleStatus(node)}
            disabled={updating === node.id}
            title={
              node.status === "pending"   ? "Mark Complete" :
              node.status === "completed" ? "Mark N/A" : "Mark Pending"
            }
            className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition disabled:opacity-50 ${
              node.status === "completed"
                ? "bg-green-500 border-green-500 text-white"
                : node.status === "n/a"
                ? "bg-gray-200 border-gray-300"
                : "border-gray-300 hover:border-green-400"
            }`}>
            {node.status === "completed" && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            {node.status === "n/a" && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 5h6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {node.item_no && <span className="text-xs text-gray-400 font-mono">#{node.item_no}</span>}

              {renamingId === node.id ? (
                <>
                  <input autoFocus value={renameText} onChange={e => setRenameText(e.target.value)}
                    className="border rounded-lg px-2 py-1 text-xs outline-none" />
                  <button onClick={() => handleRename(node.id)} className="text-xs text-blue-600 font-semibold">Save</button>
                  <button onClick={() => setRenamingId(null)} className="text-xs text-gray-400">Cancel</button>
                </>
              ) : (
                <span className={`text-sm font-medium ${
                  node.status === "completed" ? "line-through text-gray-400" :
                  node.status === "n/a" ? "line-through text-gray-300" : "text-gray-800"
                }`}>{node.item_name}</span>
              )}

              {node.status === "n/a" && (
                <span className="px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded text-xs">N/A</span>
              )}
              {node.is_milestone && (
                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-semibold">💰 Milestone</span>
              )}
            </div>

            {node.assigned_to_name && node.status !== "completed" && (
              <p className="text-xs text-blue-500 mt-0.5">👤 {node.assigned_to_name}</p>
            )}
            {node.status === "completed" && node.completed_by_name && (
              <p className="text-xs text-green-600 mt-0.5">✓ {node.completed_by_name}</p>
            )}

            {assignOpen === node.id && (
              <div className="mt-2 bg-white border rounded-xl shadow-lg p-2 z-10">
                <p className="text-xs text-gray-400 mb-2 font-semibold">Assign to:</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {teamMembers.map(m => (
                    <button key={m.id} onClick={() => handleAssign(node.id, m)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-green-50 flex items-center gap-2 ${
                        node.assigned_to === m.id ? "bg-green-50 text-green-700 font-semibold" : "text-gray-700"
                      }`}>
                      <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center font-semibold text-gray-500 shrink-0">
                        {m.name?.[0]?.toUpperCase()}
                      </span>
                      <span>{m.name}</span>
                      <span className="text-gray-400 ml-auto">{m.role}</span>
                    </button>
                  ))}
                  {node.assigned_to && (
                    <button onClick={() => handleAssign(node.id, { id: "", name: "" })}
                      className="w-full text-left px-3 py-2 rounded-lg text-xs text-red-400 hover:bg-red-50">
                      ✕ Remove assignment
                    </button>
                  )}
                </div>
              </div>
            )}

            {node.remarks && <p className="text-xs text-gray-400 mt-1 italic">"{node.remarks}"</p>}

            {remarksOpen === node.id && (
              <div className="mt-2 flex gap-2">
                <input autoFocus value={remarkText} onChange={e => setRemarkText(e.target.value)}
                  placeholder="Add remark..."
                  className="flex-1 border rounded-lg p-2 text-xs outline-none focus:ring-1 focus:ring-blue-300" />
                <button onClick={() => handleSaveRemark(node.id)}
                  className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs font-semibold">Save</button>
                <button onClick={() => { setRemarksOpen(null); setRemarkText(""); }}
                  className="px-3 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs">Cancel</button>
              </div>
            )}

            {node.is_milestone && node.status === "completed" && (
              <div className="mt-3">
                <button
                  onClick={() => navigate("/billing/create", {
                    state: {
                      from_project: true,
                      project_id:   id,
                      project_name: project?.project_name,
                      client_id:    project?.client_id,
                      client_name:  project?.client_name,
                      site_id:      project?.site_id,
                      site_name:    project?.site_name,
                      chargeable_amount: project?.total_value
                        ? Math.round(Number(project.total_value) * (node.milestone_percent || 0) / 100)
                        : 0,
                      service_complaint: node.milestone_label || node.item_name,
                    }
                  })}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700">
                  🧾 Generate {node.milestone_label || "Milestone"} Invoice
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-1 shrink-0 mt-1">
            <button onClick={() => setAssignOpen(assignOpen === node.id ? null : node.id)}
              title="Assign to team member"
              className={`text-xs ${node.assigned_to_name ? "text-blue-500" : "text-gray-300 hover:text-blue-400"}`}>👤</button>
            <button onClick={() => { setRemarksOpen(remarksOpen === node.id ? null : node.id); setRemarkText(node.remarks || ""); }}
              className="text-gray-300 hover:text-blue-500 text-xs">💬</button>
            {isManager && !node.is_milestone && (
              <button onClick={() => handleConvertToCategory(node)}
                title="Convert to category — add sub-items (e.g. 1st Floor, 2nd Floor)"
                className="text-gray-300 hover:text-purple-500 text-xs">🔀</button>
            )}
            {isManager && (
              <>
                <button onClick={() => handleMoveNode(node, siblings, index, "up")}
                  disabled={isFirst}
                  className="text-gray-300 hover:text-blue-500 text-xs disabled:opacity-20 disabled:hover:text-gray-300">↑</button>
                <button onClick={() => handleMoveNode(node, siblings, index, "down")}
                  disabled={isLast}
                  className="text-gray-300 hover:text-blue-500 text-xs disabled:opacity-20 disabled:hover:text-gray-300">↓</button>
                <button onClick={() => { setRenamingId(node.id); setRenameText(node.item_name); }}
                  className="text-gray-300 hover:text-blue-500 text-xs">✏️</button>
                <button onClick={() => handleDelete(node)}
                  className="text-gray-300 hover:text-red-500 text-xs">🗑</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={embedded ? "space-y-5" : "p-4 md:p-6 space-y-5 max-w-4xl mx-auto"}>

      {/* HEADER */}
      <div className="flex justify-between items-start">
        <div>
          {!embedded && (
            <button onClick={() => navigate(`/projects/${id}`)}
              className="text-gray-400 text-sm hover:text-gray-600 mb-1 block">
              ← {project?.project_name || "Project"}
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-800">
            {isTech ? "My Checklist" : "Project Checklist"}
          </h1>
          <p className="text-sm text-gray-500">{project?.client_name}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-green-700">{pct}%</div>
          <div className="text-xs text-gray-400">
            {completed}/{applicable} done
            {naItems > 0 && <span className="ml-1 text-gray-300">· {naItems} N/A</span>}
          </div>
        </div>
      </div>

      {/* PROGRESS BAR */}
      <div className="w-full bg-gray-100 rounded-full h-3">
        <div className="bg-green-500 h-3 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>

      {/* TREE */}
      {visibleTree.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">📋</div>
          <p>No checklist items found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleTree.map((node, index) => renderNode(node, 0, visibleTree, index))}
        </div>
      )}

      {/* ADD TOP-LEVEL CATEGORY (owner/manager only) */}
      {isManager && (
        addFormOpen === "root" ? (
          <div className="bg-white rounded-xl border border-gray-100 p-3 flex gap-2 items-center">
            <input autoFocus value={addName} onChange={e => setAddName(e.target.value)}
              placeholder="New category name..."
              className="flex-1 border rounded-lg text-sm p-2 outline-none" />
            <button onClick={() => handleAddNode("root")}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold">Add Category</button>
            <button onClick={() => { setAddFormOpen(null); setAddName(""); }}
              className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm">Cancel</button>
          </div>
        ) : (
          <button onClick={() => { setAddFormOpen("root"); setAddNodeType("category"); }}
            className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500">
            + Add Category
          </button>
        )
      )}
    </div>
  );
}